
  import { defineComponent } from 'vue'
  import TodayDeliveryApi, { PolicyAgreement, SellerPolicy, SoldoutAction, todayDeliveryError } from '@/api/TodayDeliveryApi'
  import TodayDeliveryAgreementPanel from '@/components/TodayDeliveryAgreementPanel.vue'
  import { cancelTodayDeliveryStart, settleTodayDeliveryStart, todayDeliveryStartState } from '@/composables/useTodayDeliveryStart'
  import { TODAY_DELIVERY_DEFAULT_CUTOFF } from '@/constants/todayDelivery'
  import { POLICY_SECTION, START_SETUP } from '@/constants/todayDeliveryMessages'
  import { cutoffError, cutoffItems, normalizeCutoff, normalizeSoldoutAction, soldoutActionItems } from '@/utils/todayDelivery'

  /**
   * 오늘출발 시작 설정 팝업 (PM 2026-10-13).
   *
   * [오늘출발 시작] 은 정책 저장 1회다 — `program_status: 'ON'` + 1단계 값 + 동의 버전. 출고지 4칸은 화면에서 받지 않으므로
   * 열 때 읽은 정책 값을 그대로 되돌려 보낸다(없으면 null — 서버 저장이 항상 덮어써 기존 값이 지워지지 않게).
   * 성공하면 닫고 'active', [취소] 면 'cancelled' 로 끝난다(`settleTodayDeliveryStart`). 실패는 창 안에 알리고 창을 둔다.
   */
  export default defineComponent({
    name: 'TodayDeliveryStartDialog',

    components: { TodayDeliveryAgreementPanel },

    data() {
      return {
        state: todayDeliveryStartState,
        step: 1 as 1 | 2,
        form: { order_cutoff_time: TODAY_DELIVERY_DEFAULT_CUTOFF, default_soldout_action: 'SOLD_OUT' as SoldoutAction },
        /** 2단계 동의 본문이 알려 준 동의 성립 여부(문안을 받았고 필수 카드를 모두 확인) */
        agreementComplete: false,
        busy: false,
        message: '',
        cutoffSelectItems: cutoffItems(),
        soldoutSelectItems: soldoutActionItems(),
        msg: START_SETUP,
        policyMsg: POLICY_SECTION,
        /** 라우트 이동 감시 해제 함수 */
        removeRouteHook: null as (() => void) | null,
      }
    },

    computed: {
      policy(): SellerPolicy | null {
        return this.state.policy
      },
      agreement(): PolicyAgreement | null {
        return this.policy?.policy_agreement || null
      },
      /** 서버 문안의 버전만 인정한다 — 공백뿐이면 버전이 아니다(동의 본문의 `agreementVersion` 과 같은 규칙) */
      agreementVersion(): string {
        return String(this.agreement?.version ?? '').trim()
      },
      /** 마감시각은 30일에 1회 — 지금 바꿀 수 없으면 칸을 닫는다(구 응답처럼 값이 없으면 연다) */
      cutoffChangeAvailable(): boolean {
        return this.policy?.cutoff_change_available !== false
      },
      canStart(): boolean {
        return this.step === 2 && this.agreementComplete && !!this.agreementVersion && !this.busy
      },
    },

    watch: {
      // 진입점이 팝업을 새로 열었다 — 단계·입력을 처음부터
      'state.session'() {
        this.reset()
      },
    },

    created() {
      // 다른 페이지로 옮기면 기다리는 시작 설정(정책 확인 중·팝업)을 취소로 끝낸다 — 늦은 정책 응답이 새 화면 위에 팝업을 열지 않게.
      // 같은 페이지의 쿼리 변경(검색·페이지 이동)과 실패한 이동(가드·이탈 확인 거절 — 화면은 그대로)은 그대로 둔다
      this.removeRouteHook =
        this.$router?.afterEach((to, from, failure) => {
          if (!failure && to.path !== from.path) cancelTodayDeliveryStart()
        }) || null
    },

    beforeUnmount() {
      this.removeRouteHook?.()
    },

    methods: {
      /** 1단계부터 다시 — 초기값은 진입점이 넘긴 값(배송정책 관리의 지금 폼) > 정책 응답 > 기본값(마감시각 14:00 · 품절 유지) */
      reset() {
        const prefill = this.state.prefill
        const policy = this.state.policy
        this.step = 1
        this.agreementComplete = false
        this.busy = false
        this.message = ''
        this.form = {
          order_cutoff_time:
            normalizeCutoff(prefill?.order_cutoff_time) || normalizeCutoff(policy?.order_cutoff_time) || TODAY_DELIVERY_DEFAULT_CUTOFF,
          default_soldout_action: normalizeSoldoutAction(prefill?.default_soldout_action || policy?.default_soldout_action),
        }
      },

      cancel() {
        if (this.busy) return
        settleTodayDeliveryStart('cancelled')
      },

      /** [다음] — 마감시각을 확인하고 정책 동의로 */
      next() {
        if (this.busy) return
        const error = cutoffError(this.form.order_cutoff_time)
        this.message = error
        if (error) return
        this.agreementComplete = false
        this.step = 2
      },

      /** [이전] — 고른 값은 그대로 두고 1단계로(동의 카드는 다시 오면 모두 해제한 채 시작한다) */
      back() {
        if (this.busy) return
        this.message = ''
        this.agreementComplete = false
        this.step = 1
      },

      /** [오늘출발 시작] — 정책 저장 1회. 서버가 문구를 주면(참여 제한·중지·마감시각 30일 1회 등) 그 문장을 창 안에 보여 준다 */
      async start() {
        // 버튼 비활성화와 별개로 한 번 더 막는다
        if (!this.canStart) return
        const policy = this.policy
        // 저장하는 사이 페이지를 옮겨 이 창이 닫혔거나 다른 진입점의 창으로 바뀌면 결과를 그 창에 쓰지 않는다
        const session = this.state.session
        const current = () => session === this.state.session && this.state.open
        this.busy = true
        this.message = ''
        try {
          await new TodayDeliveryApi().putPolicy(this.$store.getters.sellerNo, {
            program_status: 'ON',
            order_cutoff_time: this.form.order_cutoff_time,
            default_soldout_action: this.form.default_soldout_action,
            policy_agreed_version: this.agreementVersion,
            release_zip_code: policy?.release_zip_code || null,
            release_address_1: policy?.release_address_1 || null,
            release_address_2: policy?.release_address_2 || null,
            release_tel_no: policy?.release_tel_no || null,
          })
        } catch (error) {
          const { error_code, message } = todayDeliveryError(error)
          if (current()) this.message = error_code && message ? message : START_SETUP.saveFailed
          return
        } finally {
          // 앞 창의 늦은 저장이 새 창의 저장 중 표시를 풀지 않게 — 새 창은 열릴 때(reset) 따로 푼다
          if (current()) this.busy = false
        }
        if (current()) settleTodayDeliveryStart('active')
      },
    },
  })
